import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, EmptyState } from "@/components/common/EmptyState";
import CaseFormDialog from "@/pages/CaseFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CASE_STATUSES, fmtDate } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { Plus, Search, LayoutGrid, List, Briefcase, Filter } from "lucide-react";

export default function Cases() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cases, setCases] = useState(null);
  const [search, setSearch] = useState(params.get("q") || "");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState("list");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/cases", { params: { search: search || undefined, status } });
    setCases(data);
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cases-page">
      <PageHeader
        title="Cases"
        subtitle="Manage all your legal matters in one place."
        actions={
          <Button onClick={() => setDialogOpen(true)} data-testid="add-case-btn" className="bg-gold text-navy hover:bg-gold-dark">
            <Plus className="mr-1.5 h-4 w-4" /> Add New Case
          </Button>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="cases-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search case number, title, client, court…" className="pl-9" />
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[150px]" data-testid="cases-status-filter"><Filter className="mr-1 h-3.5 w-3.5" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {CASE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-md border border-border">
            <button onClick={() => setView("list")} className={cn("flex h-9 w-9 items-center justify-center rounded-l-md", view === "list" ? "bg-navy text-white" : "text-muted-foreground")} data-testid="view-list-btn"><List className="h-4 w-4" /></button>
            <button onClick={() => setView("grid")} className={cn("flex h-9 w-9 items-center justify-center rounded-r-md", view === "grid" ? "bg-navy text-white" : "text-muted-foreground")} data-testid="view-grid-btn"><LayoutGrid className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {!cases ? <LoadingState /> : cases.length === 0 ? (
        <EmptyState icon={Briefcase} title="No cases yet" description="Add your first case to start tracking hearings and tasks." testid="cases-empty"
          action={<Button onClick={() => setDialogOpen(true)} className="bg-navy text-white hover:bg-navy-light"><Plus className="mr-1.5 h-4 w-4" /> Add New Case</Button>} />
      ) : view === "list" ? (
        <Card className="overflow-hidden border-border shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Case</th>
                  <th className="px-4 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Court</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Next Hearing</th>
                  <th className="px-4 py-3 font-medium">Advocate</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {cases.map((c) => (
                  <tr key={c.id} data-testid={`case-row-${c.id}`} onClick={() => navigate(`/cases/${c.id}`)} className="cursor-pointer transition-colors hover:bg-secondary/40">
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy">{c.title}</p>
                      <p className="text-xs text-muted-foreground">{c.case_number}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.client_name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.court || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.case_type || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(c.next_hearing_date)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.advocate_name || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {cases.map((c) => (
            <Card key={c.id} data-testid={`case-card-${c.id}`} onClick={() => navigate(`/cases/${c.id}`)} className="cursor-pointer border-border p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold">
              <div className="flex items-start justify-between">
                <span className="text-xs font-medium text-muted-foreground">{c.case_number}</span>
                <StatusBadge status={c.status} />
              </div>
              <h3 className="mt-2 font-display text-xl leading-tight text-navy">{c.title}</h3>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                <p>{c.client_name || "No client"}</p>
                <p>{c.court || "—"}</p>
                <p className="text-navy">Next hearing: {fmtDate(c.next_hearing_date)}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CaseFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </div>
  );
}
